/**
 * RingCentral Embeddable, docked in the CRM (docs/ringcentral-plan.md, steps
 * 1–2).
 *
 * Loads the widget for a signed-in user when VITE_RINGCENTRAL_CLIENT_ID is set,
 * passes its events to lib/ringcentral (login, calling mode, dialer state —
 * what the header Call button needs), and shows the one-line notices that
 * module raises: not signed in, microphone blocked.
 *
 * Step 3: every finished call is logged (POST /api/ringcentral/calls — the
 * server writes the feed line), then "Notes about your call" opens. A missed
 * inbound call is logged with no modal: there is nothing to note.
 * `ringcentral:call-logged` (detail: { leadId }) tells an open lead file to
 * refresh its feed. Every message the widget posts (types starting `rc-`) is logged to
 * the console as `[RingCentral] <type>` with its payload as JSON, and kept in
 * window.__rcEvents so a whole session can be copied in one go
 * (DevTools: copy(__rcEvents)). That log is what decides the field names the
 * call logging in step 3 is built on.
 *
 * Each user signs in with their own RingCentral login, inside the widget
 * (Authorization Code with PKCE, Embeddable's default; tokens stay in the
 * widget's own storage). Calling mode is the user's choice in the widget's
 * Calling Settings; `browser` is only the starting value.
 *
 * DESKTOP ONLY. On mobile (lib/ringcentral isMobileDevice) the adapter script
 * is never loaded, so there is no badge or panel; the header Call button opens
 * the RingCentral app instead, and all that renders here is the bar offering
 * the phone's dialer when that app did not open (MobileCallFallback). Texting
 * is server-side and unaffected.
 *
 * SMS and the whole Messages area are switched off (disableMessages,
 * disableReadText): texts go server-side from the company number (plan §0
 * items 1–2). Meetings and Team Messaging are off too.
 */

import React, { useEffect, useState } from 'react';
import {
  isMobileDevice, ringCentralPhoneEnabled, ringCentralAppCalls, clearDialFallback, noteWidgetEvent, useRingCentral, setMicState, clearNotice, micBlockedText, MicState,
  setCallEndedHandler, showNotice, EndedCall,
} from '../lib/ringcentral';
import { useApiClient } from '../hooks/useApi';
import type { RingCentralCallLogged } from '../services/api';
import { CallNotesModal } from './CallNotesModal';

const WIDGET_ORIGIN = 'https://apps.ringcentral.com';
const ADAPTER_URL = 'https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js';
const SCRIPT_ID = 'rc-embeddable-adapter';
const PREFIX = '[RingCentral]';

declare global {
  interface Window {
    RCAdapter?: { dispose?: () => void };
    __rcEvents?: { at: string; type: string; data: unknown }[];
  }
}

const clientId = (import.meta.env.VITE_RINGCENTRAL_CLIENT_ID ?? '').trim();

/** The plan's §D5 settings. Flags are `1`, as in the Embeddable docs. */
function adapterSrc(): string {
  const params = new URLSearchParams({
    clientId,
    disableMessages: '1',
    disableReadText: '1',
    disableMeeting: '1',
    disableGlip: '1',
    defaultCallWith: 'browser',
    disableInactiveTabCallEvent: '1',
    multipleTabsSupport: '1',
    // rc-calling-settings-notify then carries myLocation: OUR number in RingOut,
    // which must never be matched as a client (plan §0 item 3).
    showMyLocationNumbers: '1',
  });
  return `${ADAPTER_URL}?${params.toString()}`;
}

/** A JSON copy of the payload, so the log can be pasted as text. */
function snapshot(data: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    return String(data);
  }
}

// Module-level so React StrictMode's dev-only mount → unmount → mount does not
// load the widget twice or tear it down: the unmount schedules the dispose and
// the immediate remount cancels it. Only a real unmount (logout) gets through.
let injected = false;
let disposeTimer: ReturnType<typeof setTimeout> | null = null;

export function RingCentralDock() {
  useEffect(() => {
    if (clientId && isMobileDevice) console.info(`${PREFIX} mobile device — phone widget not loaded (Call opens the dialer).`);
  }, []);
  if (ringCentralPhoneEnabled) return <Dock />;
  return ringCentralAppCalls ? <MobileCallFallback /> : null;
}

/**
 * Mobile: the RingCentral app did not open after Call. Offers the phone's own
 * dialer (the personal number) — the user's choice, never automatic. Gone as
 * soon as the page goes to the background (the app opened after all, or the
 * dialer did).
 */
function MobileCallFallback() {
  const rc = useRingCentral();
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') clearDialFallback(); };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, []);
  const notice = rc.notice;
  if (notice) {
    return (
      <div role="alert" style={{ ...bar, background: 'var(--crm-red-tint)', color: 'var(--crm-text)', border: '1px solid var(--crm-red-line)' }}>
        <span style={{ flex: 1 }}>{notice.text}</span>
        <button onClick={clearNotice} style={barButton} aria-label="Dismiss">OK</button>
      </div>
    );
  }
  const f = rc.dialFallback;
  if (!f) return null;
  return (
    <div role="status" style={{ ...bar, flexWrap: 'wrap', background: 'var(--crm-amber-tint)', color: 'var(--crm-text)', border: '1px solid var(--crm-amber-line)' }}>
      <span style={{ flex: '1 1 100%' }}>The RingCentral app didn’t open. Is it installed and signed in? Calling from your phone’s dialer shows your own number, not the company’s.</span>
      <a href={f.tel} onClick={() => clearDialFallback()} style={{ ...barButton, textDecoration: 'none' }}>Call from my phone</a>
      <button onClick={clearDialFallback} style={barButton}>Cancel</button>
    </div>
  );
}

function Dock() {
  useEffect(() => {
    if (!clientId) {
      console.info(`${PREFIX} VITE_RINGCENTRAL_CLIENT_ID is not set — widget not loaded.`);
      return;
    }

    if (disposeTimer) { clearTimeout(disposeTimer); disposeTimer = null; }
    window.__rcEvents = window.__rcEvents ?? [];

    const onMessage = (e: MessageEvent) => {
      const type = (e.data as any)?.type;
      if (typeof type !== 'string' || !type.startsWith('rc-')) return;
      // The widget's frame, or the adapter script running in this page. Any
      // other frame is not the phone and must not move its state.
      if (e.origin !== WIDGET_ORIGIN && e.origin !== window.location.origin) return;
      const data = snapshot(e.data);
      window.__rcEvents!.push({ at: new Date().toISOString(), type, data });
      console.log(`${PREFIX} ${type}`, JSON.stringify(data));
      noteWidgetEvent(e.data);
    };
    window.addEventListener('message', onMessage);

    if (!injected) {
      injected = true;
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = adapterSrc();
      script.async = true;
      script.onload = () => console.info(`${PREFIX} adapter loaded`);
      script.onerror = () => console.error(`${PREFIX} adapter failed to load from ${ADAPTER_URL}`);
      document.head.appendChild(script);
      console.info(`${PREFIX} loading widget (client id ending …${clientId.slice(-4)})`);
    }

    // Leaving the signed-in shell (CRM logout) removes the widget, so the next
    // person at this browser does not inherit a docked phone. Their RingCentral
    // sign-in lives in the widget's storage and is RingCentral's to manage.
    return () => {
      window.removeEventListener('message', onMessage);
      disposeTimer = setTimeout(() => {
        disposeTimer = null;
        try { window.RCAdapter?.dispose?.(); } catch { /* already gone */ }
        document.getElementById(SCRIPT_ID)?.remove();
        injected = false;
        console.info(`${PREFIX} widget removed`);
      }, 0);
    };
  }, []);

  useMicrophoneWatch();
  const notes = useCallLogging();
  return (
    <>
      <Notices />
      {notes.current && <CallNotesModal key={notes.current.call.id} entry={notes.current} onClose={notes.close} />}
    </>
  );
}

const announce = (leadId: string | null | undefined) =>
  window.dispatchEvent(new CustomEvent('ringcentral:call-logged', { detail: { leadId: leadId ?? null } }));

/**
 * Finished calls → the server → the notes modal, one at a time (a second call
 * ending while the first modal is open waits its turn).
 */
function useCallLogging() {
  const api = useApiClient();
  const [queue, setQueue] = useState<RingCentralCallLogged[]>([]);

  useEffect(() => {
    if (!clientId) return;
    setCallEndedHandler((ended: EndedCall) => {
      api.logRingCentralCall(ended)
        .then((res) => {
          const logged = res.data;
          if (!logged) return;
          console.info(`${PREFIX} call logged`, JSON.stringify({ id: logged.call?.id, lead: logged.lead?.id ?? null, result: logged.call?.result, created: logged.created }));
          announce(logged.call?.lead_id);
          const missedInbound = logged.call?.direction === 'Inbound' && logged.call?.result === 'missed';
          // A call from an ignored number (069): stored, nothing to note.
          if (logged.call?.dismissed_at) return;
          // Another tab already logged (and asked about) this call: no second modal.
          if (!missedInbound && logged.created) setQueue((q) => [...q, logged]);
        })
        .catch((e: any) => {
          console.error(`${PREFIX} could not log call ${ended.telephony_session_id}:`, e?.message ?? e);
          showNotice('warn', `This call could not be logged in the CRM: ${e?.message ?? 'unknown error'}`);
        });
    });
    return () => setCallEndedHandler(null);
  }, [api]);

  return {
    current: queue[0] ?? null,
    close: (saved: RingCentralCallLogged | null) => {
      if (saved) announce(saved.call?.lead_id);
      setQueue((q) => q.slice(1));
    },
  };
}

/**
 * The browser's microphone permission for this page — which, with the
 * widget's iframe allowed `microphone`, is what the Web Phone gets. Watched
 * live, so allowing it in the address bar clears the warning without a call.
 */
function useMicrophoneWatch() {
  useEffect(() => {
    if (!clientId) return;
    let status: PermissionStatus | null = null;
    const update = () => setMicState((status?.state as MicState) ?? 'unknown');
    navigator.permissions?.query({ name: 'microphone' as PermissionName })
      .then((s) => { status = s; update(); s.addEventListener('change', update); })
      .catch(() => setMicState('unknown')); // e.g. a browser that cannot query it
    return () => status?.removeEventListener('change', update);
  }, []);
}

/** Ask once, from a click, so the browser shows its own Allow prompt. */
async function askForMicrophone() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    setMicState('granted');
  } catch {
    setMicState('denied');
  }
}

const bar: React.CSSProperties = {
  position: 'fixed', left: '50%', bottom: '16px', transform: 'translateX(-50%)', zIndex: 2147483000,
  maxWidth: 'min(560px, calc(100vw - 32px))', display: 'flex', gap: '10px', alignItems: 'flex-start',
  padding: '10px 12px', borderRadius: '8px', fontSize: '13px', lineHeight: 1.4,
  boxShadow: '0 4px 16px var(--crm-shadow-color)',
};
const barButton: React.CSSProperties = {
  flexShrink: 0, padding: '4px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
  background: 'var(--crm-panel)', color: 'var(--crm-text)', border: '1px solid var(--crm-line)',
};

/**
 * One bar at a time, bottom centre:
 *  - a notice raised by startCall (not signed in, microphone blocked, …);
 *  - otherwise, while signed in with Browser calling, a standing warning when
 *    the microphone is blocked, or a prompt to allow it when it has never been
 *    asked — the Web Phone shows "Unavailable" until it is.
 */
function Notices() {
  const rc = useRingCentral();
  const browserCalling = rc.loggedIn && rc.callWith === 'browser';

  if (rc.notice) {
    const warn = rc.notice.kind === 'warn';
    return (
      <div role={warn ? 'alert' : 'status'} style={{
        ...bar,
        background: warn ? 'var(--crm-red-tint)' : 'var(--crm-panel-2)',
        color: 'var(--crm-text)',
        border: `1px solid ${warn ? 'var(--crm-red-line)' : 'var(--crm-line)'}`,
      }}>
        <span style={{ flex: 1 }}>{rc.notice.text}</span>
        <button onClick={clearNotice} style={barButton} aria-label="Dismiss">OK</button>
      </div>
    );
  }

  if (browserCalling && rc.mic === 'denied') {
    return (
      <div role="alert" style={{ ...bar, background: 'var(--crm-red-tint)', color: 'var(--crm-text)', border: '1px solid var(--crm-red-line)' }}>
        <span style={{ flex: 1 }}><b>Microphone blocked.</b> {micBlockedText}</span>
      </div>
    );
  }

  if (browserCalling && rc.mic === 'prompt') {
    return (
      <div role="status" style={{ ...bar, background: 'var(--crm-amber-tint)', color: 'var(--crm-text)', border: '1px solid var(--crm-amber-line)' }}>
        <span style={{ flex: 1 }}>RingCentral needs your microphone for browser calls. Click <b>Allow microphone</b>, then choose Allow in the browser prompt.</span>
        <button onClick={askForMicrophone} style={barButton}>Allow microphone</button>
      </div>
    );
  }

  return null;
}
